import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaGift } from "react-icons/fa";
import { motion } from "framer-motion";

const logoSrc = (() => {
  try {
    return require("/logo.png");
  } catch (error) {
    return "/logo.png";
  }
})();

const Navbar = ({ onNewsletterClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(!(currentScrollY > lastScrollY && currentScrollY > 50));
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
  };

  const location = useLocation();
  
  const navItems = [
    { to: "/ai-hairstyle-reviews", text: "Reviews", external: true },
    { to: "/random-hairstyle-generator", text: "Free Hairstyle Generator", external: true },
    { to: "/studio-hair-salon", text: "Gallery" },
    ...(user ? [{ to: "/try-on", text: "Try On" }, { to: "/dashboard", text: "Dashboard" }] : []),
  ];

  return (
    <motion.nav
      className={`bg-white shadow-soft fixed top-0 left-0 w-full z-50 transition-transform duration-300 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="container">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center hover-scale">
              <img src={logoSrc} alt="AI Hairstyle Studio Logo" className="h-10 w-auto mr-2" />
              <span className="text-xl font-bold text-teal">AI Hairstyle Studio</span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) =>
              item.external ? (
                <a key={item.to} href={item.to} className="nav-link px-3 py-2" target="_blank" rel="noopener noreferrer">
                  {item.text}
                </a>
              ) : (
                <Link key={item.to} to={item.to} className="nav-link px-3 py-2">
                  {item.text}
                </Link>
              )
            )}

            {user ? (
              <button onClick={handleLogout} className="btn btn-secondary ml-4">
                Sign Out
              </button>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="nav-link">Sign In</Link>
                <Link to="/signup" className="btn btn-primary">Sign Up</Link>
              </div>
            )}

            {/* Newsletter Button (Gift Icon) */}
            <div className="relative">
              <motion.div
                initial={{ y: 0 }}
                animate={{ y: [-5, 5, -5] }}
                transition={{ repeat: Infinity, duration: 1 }}
                className="relative"
              >
                <FaGift 
                  className="text-3xl text-teal-500 drop-shadow-lg cursor-pointer" 
                  onClick={onNewsletterClick} 
                />
                <motion.div
                  initial={{ opacity: 0.5, scale: 1 }}
                  animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="absolute inset-0 bg-yellow-300 opacity-30 blur-md rounded-full"
                  style={{ pointerEvents: 'none' }} // Allow clicks through the background
                />
              </motion.div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray hover:text-teal focus:outline-none">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden absolute top-16 left-0 w-full bg-white shadow-lg z-40">
            <div className="flex flex-col items-center space-y-4 py-4">
              {navItems.map((item) =>
                item.external ? (
                  <a key={item.to} href={item.to} className="nav-link px-3 py-2" target="_blank" rel="noopener noreferrer">
                    {item.text}
                  </a>
                ) : (
                  <Link key={item.to} to={item.to} className="nav-link px-3 py-2" onClick={() => setIsOpen(false)}>
                    {item.text}
                  </Link>
                )
              )}

              {user ? (
                <button onClick={handleLogout} className="btn btn-secondary">
                  Sign Out
                </button>
              ) : (
                <div className="flex flex-col items-center space-y-2">
                  <Link to="/login" className="nav-link" onClick={() => setIsOpen(false)}>
                    Sign In
                  </Link>
                  <Link to="/signup" className="btn btn-primary px-4 py-2" onClick={() => setIsOpen(false)}>
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Newsletter Button in Mobile Menu */}
              <div className="relative mt-2">
                <motion.div
                  initial={{ y: 0 }}
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="relative"
                >
                  <FaGift 
                    className="text-3xl text-teal-500 drop-shadow-lg cursor-pointer" 
                    onClick={onNewsletterClick} 
                  />
                  <motion.div
                    initial={{ opacity: 0.5, scale: 1 }}
                    animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="absolute inset-0 bg-yellow-300 opacity-30 blur-md rounded-full"
                    style={{ pointerEvents: 'none' }}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
